'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Building2, Upload, CheckCircle, AlertTriangle, MapPin, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface HostSignupInputs {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  businessName: string;
  businessType: string;
  locationArea: string;
  locationAddress: string;
  storageCapacityKg: number;
  availableSpaceDescription: string;
  idType: string;
  termsAccepted: boolean;
  hostTerms: boolean;
}

interface HostSignupFormProps {
  onSwitchToLogin: () => void;
}

export default function HostSignupForm({ onSwitchToLogin }: HostSignupFormProps) {
  const { signUp } = useAuth();
  const supabase = createClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [idFileName, setIdFileName] = useState<string | null>(null);
  const [permitFileName, setPermitFileName] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<HostSignupInputs>({ defaultValues: { idType: 'national_id', businessType: 'individual', storageCapacityKg: 50 } });

  const password = watch('password');

  const onSubmit = async (data: HostSignupInputs) => {
    try {
      // Create auth account with customer role (host pending approval)
      const authData = await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: 'customer',
        phone: data.phone,
      });

      if (authData?.user?.id) {
        // Insert host application
        await supabase.from('host_applications').insert({
          user_id: authData.user.id,
          full_name: data.fullName,
          email: data.email,
          phone: `+254${data.phone}`,
          business_name: data.businessName || null,
          business_type: data.businessType,
          location_area: data.locationArea,
          location_address: data.locationAddress,
          storage_capacity_kg: data.storageCapacityKg,
          available_space_description: data.availableSpaceDescription,
          id_document_type: data.idType,
          status: 'pending',
        });

        // Notify admins
        const { data: admins } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('is_admin', true);

        if (admins && admins.length > 0) {
          const adminNotifs = admins.map((a: any) => ({
            user_id: a.id,
            type: 'host_application',
            title: 'New Host Application',
            message: `${data.fullName} has applied to become a storage host. Review their application.`,
            link: '/admin-host-applications',
          }));
          await supabase.from('notifications').insert(adminNotifs);
        }
      }

      setSubmitted(true);
      toast.success('Host application submitted! Awaiting admin approval.');
    } catch (err: any) {
      setError('root', {
        message: err?.message || 'Failed to submit application. Please try again.',
      });
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(5,150,105,0.15)' }}>
          <CheckCircle size={32} className="text-emerald-500" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Application Submitted!</h2>
        <p className="text-sm text-muted-foreground mb-2">Your host application is under review.</p>
        <p className="text-xs text-muted-foreground mb-6 max-w-xs mx-auto">
          Our admin team will review your details and notify you within 24–48 hours. Check your email for updates.
        </p>
        <div className="p-4 rounded-xl mb-6 text-left" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">What happens next?</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>✓ Admin reviews your application & documents</li>
            <li>✓ You receive approval notification via email</li>
            <li>✓ Your host dashboard is activated</li>
            <li>✓ You can start listing storage spaces</li>
          </ul>
        </div>
        <button
          onClick={onSwitchToLogin}
          className="px-6 py-2.5 text-white rounded-xl text-sm font-bold hover:opacity-90 transition-colors"
          style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)' }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Become a Host</h1>
        <p className="text-sm text-muted-foreground">Earn by renting out your spare storage space to travelers</p>
      </div>

      {errors.root && (
        <div className="px-4 py-3 rounded-xl text-sm font-medium" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#dc2626' }}>
          {errors.root.message}
        </div>
      )}

      {/* Host info banner */}
      <div className="flex items-start gap-2.5 p-3.5 rounded-xl" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
        <Building2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-700 dark:text-emerald-400 leading-relaxed">
          Host applications are reviewed by our admin team. Once approved, you can list your storage spaces and earn from travelers.
        </p>
      </div>

      {/* Personal Details */}
      <div className="p-4 rounded-xl space-y-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-bold text-foreground uppercase tracking-wide">Personal Details</p>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
          <input
            type="text"
            placeholder="e.g. Wanjiku Kamau"
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.fullName ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
            {...register('fullName', { required: 'Full name is required', minLength: { value: 3, message: 'At least 3 characters' } })}
          />
          {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
          <input
            type="email"
            placeholder="you@example.com"
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.email ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
            {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Valid email required' } })}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
          <div className="flex">
            <span className="px-3.5 py-2.5 bg-muted border border-r-0 border-input rounded-l-xl text-sm text-muted-foreground font-semibold">+254</span>
            <input
              type="tel"
              placeholder="7XX XXX XXX"
              className={`flex-1 px-3.5 py-2.5 rounded-r-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.phone ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
              {...register('phone', { required: 'Phone required', pattern: { value: /^[7][0-9]{8}$/, message: 'Valid Kenyan number (7XXXXXXXX)' } })}
            />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Min. 8 characters"
              className={`w-full px-3.5 py-2.5 pr-10 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.password ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
              {...register('password', { required: 'Password required', minLength: { value: 8, message: 'Min. 8 characters' } })}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              className={`w-full px-3.5 py-2.5 pr-10 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.confirmPassword ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
              {...register('confirmPassword', { required: 'Please confirm password', validate: v => v === password || 'Passwords do not match' })}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
        </div>
      </div>

      {/* Business / Space Details */}
      <div className="p-4 rounded-xl space-y-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-bold text-foreground uppercase tracking-wide">Storage Space Details</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Business Type</label>
            <select
              className="w-full px-3.5 py-2.5 rounded-xl border border-input text-sm bg-background outline-none focus:border-emerald-500"
              {...register('businessType')}
            >
              <option value="individual">Individual / Home</option>
              <option value="shop">Shop / Store</option>
              <option value="office">Office Space</option>
              <option value="warehouse">Warehouse</option>
              <option value="hotel">Hotel / Hostel</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Capacity (kg)</label>
            <input
              type="number"
              min={10}
              max={5000}
              placeholder="50"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors ${errors.storageCapacityKg ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
              {...register('storageCapacityKg', { required: 'Required', min: { value: 10, message: 'Min 10kg' } })}
            />
            {errors.storageCapacityKg && <p className="mt-1 text-xs text-red-500">{errors.storageCapacityKg.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Business Name (optional)</label>
          <input
            type="text"
            placeholder="e.g. Kamau's Storage Hub"
            className="w-full px-3.5 py-2.5 rounded-xl border border-input text-sm bg-background outline-none focus:border-emerald-500 placeholder:text-muted-foreground"
            {...register('businessName')}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            <MapPin size={13} className="inline mr-1 text-emerald-600" />
            Area / Neighbourhood
          </label>
          <input
            type="text"
            placeholder="e.g. Westlands, Nairobi"
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.locationArea ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
            {...register('locationArea', { required: 'Area is required' })}
          />
          {errors.locationArea && <p className="mt-1 text-xs text-red-500">{errors.locationArea.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Full Address</label>
          <input
            type="text"
            placeholder="e.g. Westlands Rd, near Sarit Centre"
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.locationAddress ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
            {...register('locationAddress', { required: 'Address is required' })}
          />
          {errors.locationAddress && <p className="mt-1 text-xs text-red-500">{errors.locationAddress.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            <FileText size={13} className="inline mr-1 text-emerald-600" />
            Describe Your Space
          </label>
          <textarea
            rows={3}
            placeholder="Describe your storage space — security features, access hours, what items you can store..."
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors resize-none placeholder:text-muted-foreground ${errors.availableSpaceDescription ? 'border-red-400' : 'border-input focus:border-emerald-500'}`}
            {...register('availableSpaceDescription', { required: 'Description required', minLength: { value: 30, message: 'At least 30 characters' } })}
          />
          {errors.availableSpaceDescription && <p className="mt-1 text-xs text-red-500">{errors.availableSpaceDescription.message}</p>}
        </div>
      </div>

      {/* ID Verification */}
      <div className="p-4 rounded-xl space-y-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)' }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-foreground">Identity Verification Required</p>
            <p className="text-xs text-muted-foreground mt-0.5">All hosts must verify their identity before listing spaces.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">ID Document Type</label>
          <select
            className="w-full px-3.5 py-2.5 rounded-xl border border-input text-sm bg-background outline-none focus:border-emerald-500"
            {...register('idType')}
          >
            <option value="national_id">National ID Card</option>
            <option value="passport">Passport</option>
            <option value="alien_id">Alien ID</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Upload ID Document</label>
          <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer hover:border-emerald-500 transition-colors" style={{ borderColor: idFileName ? '#10B981' : 'var(--border)' }}>
            {idFileName ? (
              <>
                <CheckCircle size={20} className="text-emerald-500" />
                <span className="text-sm text-emerald-600 font-semibold">{idFileName}</span>
                <span className="text-xs text-muted-foreground">Click to replace</span>
              </>
            ) : (
              <>
                <Upload size={20} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upload front + back of ID</span>
                <span className="text-xs text-muted-foreground">JPG, PNG or PDF — max 5MB</span>
              </>
            )}
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e => setIdFileName(e.target.files?.[0]?.name ?? null)} />
          </label>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Business Permit (optional)</label>
          <label className="flex flex-col items-center gap-2 p-3 border-2 border-dashed rounded-xl cursor-pointer hover:border-emerald-500 transition-colors" style={{ borderColor: permitFileName ? '#10B981' : 'var(--border)' }}>
            {permitFileName ? (
              <span className="text-sm text-emerald-600 font-semibold">{permitFileName}</span>
            ) : (
              <span className="text-sm text-muted-foreground">Upload business permit (if applicable)</span>
            )}
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e => setPermitFileName(e.target.files?.[0]?.name ?? null)} />
          </label>
        </div>
      </div>

      {/* Host Terms */}
      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-input accent-emerald-600 shrink-0"
            {...register('hostTerms', { required: 'You must accept host responsibilities' })}
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to maintain a safe, clean storage space, be available during stated hours, and comply with MuyuLink's host code of conduct.
          </span>
        </label>
        {errors.hostTerms && <p className="mt-1 text-xs text-red-500">{errors.hostTerms.message}</p>}
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-input accent-emerald-600 shrink-0"
            {...register('termsAccepted', { required: 'You must accept the terms' })}
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to MuyuLink's{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 font-semibold hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 font-semibold hover:underline">Privacy Policy</a>
          </span>
        </label>
        {errors.termsAccepted && <p className="mt-1 text-xs text-red-500">{errors.termsAccepted.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 text-white rounded-xl text-sm font-bold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
        style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', boxShadow: '0 4px 16px rgba(5,150,105,0.3)' }}
      >
        {isSubmitting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Submitting Application...
          </>
        ) : (
          <>
            <Building2 size={16} />
            Apply to Become a Host
          </>
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-emerald-600 font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </form>
  );
}
